import { useAppState } from '../../../AppStateProvider';
import type { Preference } from '../../../domain/preferences';
import { useCountryPreferencesQuery } from '../../../features/preferences/useCountryPreferencesQuery';
import { useMetricsLabelsQuery } from '../../../features/preferences/useMetricsLabelsQuery';
import { useMetricsPreferencesQuery } from '../../../features/preferences/useMetricsPreferencesQuery';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';

export enum PreferenceTrackingName {
  valueProposition = 'value proposition',
  metrics = 'metrics',
  countries = 'countries',
}

export const usePreferencesTracking = () => {
  const { emailGenerationOptions } = useAppState();
  const dispatchTrackEvent = useDispatchTrackEventWithLocation();

  const { data: metrics } = useMetricsPreferencesQuery();
  const { data: labels } = useMetricsLabelsQuery();
  const { data: countries } = useCountryPreferencesQuery();

  const handleTrackPreferences = (eventName: string, eventSubName: string) =>
    dispatchTrackEvent('preferences', eventName, eventSubName);

  const handleTrackOpenPreferences = () => {
    dispatchTrackEvent('ai outreach', 'edit preferences', emailGenerationOptions.emailsToGenerate.toString());
  };

  const handleTrackClickOnPreference = (selectedPreference: Preference) => {
    handleTrackPreferences('view preference type', PreferenceTrackingName[selectedPreference]);
  };

  const handleTrackApplyPreferences = ({
    selectedPreference,
    metricsCount,
    countriesCount,
  }: {
    selectedPreference: Preference;
    metricsCount?: number;
    countriesCount?: number;
  }) => {
    handleTrackPreferences(
      'apply changes',
      `${PreferenceTrackingName[selectedPreference]}/${metricsCount || metrics?.length || 0}/${
        countriesCount || countries?.length || 0
      }`
    );
  };

  const handleTrackRemoveCountry = (country: string, countriesCount: number) =>
    handleTrackPreferences('remove country', `${country}/${countriesCount}`);
  const handleTrackAddCountry = (country: string, countriesCount: number) =>
    handleTrackPreferences('select country', `${country}/${countriesCount}`);

  const handleTrackResetValueProp = () => handleTrackPreferences('undo changes', 'reset value proposition');

  const getMetricLable = (metric: string) => labels?.find((label) => label.metric === metric)?.label || metric;

  const handleTrackRemoveMetric = (metric: string, metricsCount: number) =>
    handleTrackPreferences('remove metric', `${getMetricLable(metric)}/${metricsCount}`);

  const handleTrackAddMetric = (metric: string, metricsCount: number) =>
    handleTrackPreferences('add metric', `${getMetricLable(metric)}/${metricsCount}`);

  return {
    handleTrackOpenPreferences,
    handleTrackClickOnPreference,
    handleTrackApplyPreferences,
    handleTrackRemoveCountry,
    handleTrackAddCountry,
    handleTrackResetValueProp,
    handleTrackRemoveMetric,
    handleTrackAddMetric,
  };
};
